import React, { useState, useEffect } from 'react';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Input from '../../common/Input';
import SearchableSelect from '../../common/SearchableSelect';
import api from '../../../services/api';
import { API_ENDPOINTS } from '../../../constants/config';
import { ROLES } from '../../../constants/roles';

const EscalationMatrixManager = () => {
    const [matrices, setMatrices] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingMatrix, setEditingMatrix] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [phoneErrors, setPhoneErrors] = useState({});
    const [emailErrors, setEmailErrors] = useState({});

    useEffect(() => {
        fetchData();
        fetchUsers();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await api.get(API_ENDPOINTS.ESCALATION_MATRICES);
            setMatrices(response.data);
        } catch (error) {
            console.error("Failed to fetch escalation matrices", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await api.get(API_ENDPOINTS.USERS);
            const nonCustomerUsers = response.data.filter(u => u.role !== ROLES.CUSTOMER);
            setUsers(nonCustomerUsers.map(u => ({
                value: u.id,
                label: u.name,
                subtitle: `${u.designation ? u.designation : u.role} | ${u.email}`
            })));
        } catch (error) {
            console.error("Failed to fetch users", error);
        }
    };

    const handleOpenModal = (matrix = null) => {
        if (matrix) {
            setEditingMatrix({
                ...matrix,
                levels: [...matrix.levels]
            });
        } else {
            setEditingMatrix({
                name: '',
                levels: [{ label: '', type: 'user', value: '', custom_name: '', custom_phone: '', custom_email:'', custom_designation: '', notes: '' }]
            });
        }
        setIsModalOpen(true);
    };

    const handleAddLevel = () => {
        setEditingMatrix({
            ...editingMatrix,
            levels: [...editingMatrix.levels, { label: '', type: 'user', value: '', custom_name: '', custom_phone: '', custom_email:'', custom_designation: '', notes: '' }]
        });
    };

    const handleRemoveLevel = (index) => {
        const newLevels = editingMatrix.levels.filter((_, i) => i !== index);
        setEditingMatrix({ ...editingMatrix, levels: newLevels });
    };

    const handleLevelChange = (index, field, value) => {
        const newLevels = [...editingMatrix.levels];

        if (field === 'custom_phone') {
        value = value.replace(/\D/g, '').slice(0, 10);
        if (value.length > 0 && value.length !== 10) {
            setPhoneErrors(prev => ({ ...prev, [index]: 'Please enter a valid 10 digit phone number' }));
        } else {
            setPhoneErrors(prev => ({ ...prev, [index]: '' }));
        }}

        if (field === 'custom_email') {
            if (value.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                setEmailErrors(prev => ({ ...prev, [index]: 'Please enter a valid email address' }));
            } else {
                setEmailErrors(prev => ({ ...prev, [index]: '' }));
            }}

        newLevels[index] = { ...newLevels[index], [field]: value };
        
        // Reset value if type changes
        if (field === 'type') {
            newLevels[index].value = '';
            newLevels[index].custom_name = '';
            newLevels[index].custom_phone = '';
            newLevels[index].custom_email='';
            newLevels[index].custom_designation = '';
        }
        
        setEditingMatrix({ ...editingMatrix, levels: newLevels });
    };

    const handleSave = async () => {
        if (!editingMatrix.name.trim()) return alert("Name is required");
        if (editingMatrix.levels.length === 0) return alert("At least one level is required");
        
        setSubmitting(true);
        try {
            if (editingMatrix.id) {
                await api.put(API_ENDPOINTS.ESCALATION_MATRIX_DETAILS(editingMatrix.id), editingMatrix);
            } else {
                await api.post(API_ENDPOINTS.ESCALATION_MATRICES, editingMatrix);
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || "Failed to save matrix");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this preset?")) return;
        try {
            await api.delete(API_ENDPOINTS.ESCALATION_MATRIX_DETAILS(id));
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || "Failed to delete matrix");
        }
    };

    if (loading) return <div>Loading...</div>;

    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px'
    };

    const modalStyle = {
        background: 'var(--surface)',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>Escalation Matrix Presets</h2>
                <Button onClick={() => handleOpenModal()}>Add New Preset</Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {matrices.map(matrix => (
                    <Card key={matrix.id} title={matrix.name} padding="20px">
                        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                            {matrix.levels.length} Escalation Levels
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <Button variant="secondary" size="small" onClick={() => handleOpenModal(matrix)}>Edit</Button>
                            <Button variant="danger" size="small" onClick={() => handleDelete(matrix.id)}>Delete</Button>
                        </div>
                    </Card>
                ))}
            </div>

            {isModalOpen && (
                <div style={modalOverlayStyle}>
                    <div style={modalStyle}>
                        <div style={{ padding: '24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0 }}>{editingMatrix.id ? 'Edit Preset' : 'New Preset'}</h3>
                            <button onClick={() => setIsModalOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                            </button>
                        </div>
                        
                        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                            <Input 
                                label="Preset Name" 
                                value={editingMatrix.name} 
                                onChange={(e) => setEditingMatrix({...editingMatrix, name: e.target.value})}
                                placeholder="e.g. Standard Support, High Priority Site"
                            />

                            <div style={{ marginTop: '24px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h4 style={{ margin: 0 }}>Escalation Levels</h4>
                                    <Button variant="secondary" size="small" onClick={handleAddLevel}>+ Add Level</Button>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {editingMatrix.levels.map((level, index) => (
                                        <div key={index} style={{ 
                                            padding: '16px', 
                                            background: 'var(--background)', 
                                            borderRadius: '12px', 
                                            border: '1px solid var(--border)',
                                            position: 'relative' 
                                        }}>
                                            <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                                                <button 
                                                    onClick={() => handleRemoveLevel(index)}
                                                    style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                                                </button>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                <Input 
                                                    label="Level Label" 
                                                    value={level.label} 
                                                    onChange={(e) => handleLevelChange(index, 'label', e.target.value)}
                                                    placeholder="e.g. Level 1 - Service Desk"
                                                />
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    <label style={{ fontSize: '14px', fontWeight: '600' }}>Type</label>
                                                    <div style={{ display: 'flex', gap: '8px', background: 'var(--surface)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                                        <button 
                                                            style={{ 
                                                                flex: 1, padding: '6px', borderRadius: '6px', border: 'none', 
                                                                background: level.type === 'user' ? 'var(--primary)' : 'transparent',
                                                                color: level.type === 'user' ? 'white' : 'var(--text-secondary)',
                                                                fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleLevelChange(index, 'type', 'user')}
                                                        >System User</button>
                                                        <button 
                                                            style={{ 
                                                                flex: 1, padding: '6px', borderRadius: '6px', border: 'none', 
                                                                background: level.type === 'text' ? 'var(--primary)' : 'transparent',
                                                                color: level.type === 'text' ? 'white' : 'var(--text-secondary)',
                                                                fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleLevelChange(index, 'type', 'text')}
                                                        >Custom Text</button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: '16px' }}>
                                                {level.type === 'user' ? (
                                                    <SearchableSelect 
                                                        label="Select User"
                                                        options={users}
                                                        value={level.value}
                                                        onChange={(val) => handleLevelChange(index, 'value', val)}
                                                        placeholder="Search user by name or email..."
                                                    />
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                        <Input
                                                            label="Name"
                                                            value={level.custom_name || ''}
                                                            onChange={(e) => handleLevelChange(index, 'custom_name', e.target.value)}
                                                            placeholder="e.g. John Doe"
                                                        />
                                                        <Input
                                                            label="Phone Number"
                                                            value={level.custom_phone || ''}
                                                            onChange={(e) => handleLevelChange(index, 'custom_phone', e.target.value)}
                                                            placeholder="e.g. 9876543210"
                                                        />
                                                        {phoneErrors[index] && (
                                                            <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                                                {phoneErrors[index]}
                                                            </span>
                                                        )}
                                                        <Input
                                                            label="Email id"
                                                            value={level.custom_email || ''}
                                                            onChange={(e) => handleLevelChange(index, 'custom_email', e.target.value)}
                                                            placeholder="e.g. john.doe@gmail.com"
                                                        />
                                                        {emailErrors[index] && (
                                                            <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                                                {emailErrors[index]}
                                                            </span>
                                                        )}
                                                        <Input
                                                            label="Designation"
                                                            value={level.custom_designation || ''}
                                                            onChange={(e) => handleLevelChange(index, 'custom_designation', e.target.value)}
                                                            placeholder="e.g. Manager"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div style={{ marginTop: '0px' }}>
                                                <Input 
                                                    label="Notes (Optional)"
                                                    value={level.notes}
                                                    onChange={(e) => handleLevelChange(index, 'notes', e.target.value)}
                                                    placeholder="Escalate after 4 hours of no response"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleSave} disabled={submitting}>
                                {submitting ? 'Saving...' : 'Save Preset'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EscalationMatrixManager;
